import type { $Fetch } from 'ofetch';
import type {
  Assignment, UpsertAssignmentCommand, ListAssignmentsParams,
  AssignmentSubmission, CorrigirSubmissionCommand,
} from '@/domain/model/Assignment';
import type { AssignmentGateway } from '@/application/port/out/AssignmentGateway';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const ROOT = '/api/v1/assignments';
const SUBS = '/api/v1/assignment-submissions';

/** HTTP gateway de Atividades. Authorities `assignment:read|manage`. */
export class HttpAssignmentGateway implements AssignmentGateway {
  constructor(private readonly client: $Fetch) {}

  async list(p: ListAssignmentsParams): Promise<Assignment[]> {
    const params: Record<string, string> = {};
    if (p.type) params['type'] = p.type;
    if (p.disciplineId) params['disciplineId'] = p.disciplineId;
    if (p.status) params['status'] = p.status;
    const res = await this.client<ApiResponse<Assignment[]>>(ROOT, { method: 'GET', params });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertAssignmentCommand): Promise<Assignment> {
    const res = await this.client<ApiResponse<Assignment>>(ROOT, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertAssignmentCommand): Promise<Assignment> {
    const res = await this.client<ApiResponse<Assignment>>(`${ROOT}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async publicar(id: string): Promise<Assignment> {
    const res = await this.client<ApiResponse<Assignment>>(`${ROOT}/${encodeURIComponent(id)}/publicar`, { method: 'POST' });
    assertApiSuccess(res); return res.data;
  }
  async encerrar(id: string): Promise<Assignment> {
    const res = await this.client<ApiResponse<Assignment>>(`${ROOT}/${encodeURIComponent(id)}/encerrar`, { method: 'POST' });
    assertApiSuccess(res); return res.data;
  }
  async arquivar(id: string): Promise<void> {
    // 204 No Content — sem envelope.
    await this.client<void>(`${ROOT}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
  async listSubmissions(assignmentId: string): Promise<AssignmentSubmission[]> {
    const res = await this.client<ApiResponse<AssignmentSubmission[]>>(
      `${ROOT}/${encodeURIComponent(assignmentId)}/submissions`, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async corrigirSubmission(submissionId: string, cmd: CorrigirSubmissionCommand): Promise<AssignmentSubmission> {
    const res = await this.client<ApiResponse<AssignmentSubmission>>(
      `${SUBS}/${encodeURIComponent(submissionId)}/corrigir`, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async baixarArquivoSubmission(submissionId: string): Promise<Blob> {
    // Proxy do backend (streaming) — a storageKey crua não é URL navegável.
    return this.client(`${SUBS}/${encodeURIComponent(submissionId)}/download`, {
      method: 'GET',
      responseType: 'blob',
    });
  }
}
